import { useState } from 'react';
import type { ItemKind } from '../types';
import { api } from '../api';
import { useI18n } from '../i18n';

interface Props {
  onCaptured?: () => void;
  compact?: boolean;
}

const KINDS: { id: ItemKind; labelKey: string }[] = [
  { id: 'task', labelKey: 'capture.kind.task' },
  { id: 'note', labelKey: 'capture.kind.note' },
  { id: 'idea', labelKey: 'capture.kind.idea' },
];

export function QuickCapture({ onCaptured, compact }: Props) {
  const { t } = useI18n();
  const [content, setContent] = useState('');
  const [kind, setKind] = useState<ItemKind>('task');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = content.trim();
    if (!text) return;
    setSaving(true);
    setError(null);
    try {
      const tags = tagInput
        .split(/[,\s]+/)
        .map((t) => t.replace(/^#/, '').trim())
        .filter(Boolean);
      await api.capture(text, kind, tags);
      setContent('');
      setTagInput('');
      onCaptured?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="stack">
      <textarea
        className="textarea"
        placeholder={compact ? t('capture.placeholder') : t('capture.placeholder.long')}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            void submit(e as unknown as React.FormEvent);
          }
        }}
      />
      <div className="field-row">
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value as ItemKind)}
          style={{ flex: '0 0 140px' }}
        >
          {KINDS.map((k) => (
            <option key={k.id} value={k.id}>
              {t(k.labelKey)}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder={t('capture.tags.placeholder')}
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={saving || !content.trim()}>
          {saving ? t('capture.saving') : t('capture.button')}
          <span className="kbd">⌘↵</span>
        </button>
      </div>
      {error && <div className="error-banner">{error}</div>}
    </form>
  );
}
